import React, { useRef, useState } from 'react'
import { Base, Checkbox } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { NewReview, ReviewStatus } from '@/domain/review'
import PersonFilledIcon from '@/assets/images/icons/PersonFilledIcon'

const {
  Button,
  Textarea,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogFooter
} = Base

interface ModalProps {
  index: number
  isOpen: boolean
  setIsOpen: (open: number) => void
  reviews: NewReview[]
  setReviews: (reviews: NewReview[]) => void
  updateReviews: (
    reviews: NewReview[],
    setReviews: (reviews: NewReview[]) => void,
    index: number,
    status: ReviewStatus,
    rejectReason?: string,
    showRejectReason?: boolean
  ) => void
}

const RejectReasonModal: React.FC<ModalProps> = ({
  index,
  isOpen,
  setIsOpen,
  reviews,
  setReviews,
  updateReviews
}) => {
  const { t } = useTranslation()

  const rejectReason = useRef<HTMLTextAreaElement>(null)

  const [sendToCustomer, setSendToCostumer] = useState(false)
  const [reasonError, setReasonError] = useState(false)

  // Dismissing leaves the item unreviewed rather than declined, so the row does not
  // keep a decision the operator never confirmed.
  const handleCloseModal = () => {
    updateReviews(reviews, setReviews, index, 'PENDING')
    setIsOpen(-1)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (rejectReason.current?.value) {
      updateReviews(
        reviews,
        setReviews,
        index,
        'DECLINED',
        rejectReason.current.value,
        sendToCustomer
      )

      setIsOpen(-1)

      return
    }

    setReasonError(true)
  }

  return (
    <DialogRoot
      open={isOpen}
      disablePointerDismissal
      onOpenChange={(open) => {
        if (!open) handleCloseModal()
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup>
          <form className="w-[28rem] space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <DialogTitle>{t('reject_modal.title')}</DialogTitle>
              <div className="flex flex-col gap-2">
                <Textarea
                  ref={rejectReason}
                  autoFocus
                  rows={6}
                  className="resize-none font-sans"
                  name="reject-reason"
                  id="reject-reason"
                  aria-invalid={reasonError}
                  aria-describedby={
                    reasonError ? 'reject-reason-error' : undefined
                  }
                  placeholder={t('reject_modal.input_placeholder')}
                  onChange={(e) => {
                    if (e.currentTarget.value.length > 0 && reasonError) {
                      setReasonError(false)
                    }
                  }}
                />
                {reasonError ? (
                  <span
                    id="reject-reason-error"
                    className="pl-1 text-xs leading-none text-red-500"
                  >
                    {t('reject_modal.reason_required')}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col gap-2 rounded-lg border border-solid border-neutral-300 bg-white">
                <div className="flex items-center gap-2 rounded-t-lg bg-neutral-100 px-2 py-1">
                  <span className="size-6 rounded-full bg-yellow-500 p-0.5 text-white">
                    <PersonFilledIcon />
                  </span>
                  <span className="text-sm font-bold text-neutral-900">
                    {t('reject_modal.show_reason_to_customer')}
                  </span>
                </div>
                <div className="flex gap-2 p-2">
                  <span>
                    <Checkbox
                      id="showReasonToCustomer"
                      className={
                        'flex size-5 cursor-pointer items-center justify-between rounded border border-solid border-neutral-700 text-white data-[checked]:bg-neutral-900'
                      }
                      checked={sendToCustomer}
                      styled={false}
                      onChange={setSendToCostumer}
                    />
                  </span>
                  <label
                    htmlFor="showReasonToCustomer"
                    className="-mt-1 text-sm font-light text-neutral-900"
                  >
                    {t('reject_modal.show_reason_description')}
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseModal}
              >
                {t('reject_modal.cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('reject_modal.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}

export default RejectReasonModal
